// The producer's actions (POST /battles/{code}/host/{action}). A refusal keeps the server's reason, so the page can tell a
// start held back for clinical sign-off (and whether hosts may override it) from any other refusal. Start can carry
// { override: true } to begin without sign-off; the server records that.
import type { HostAction, StateSnapshot } from '@shared/types';
import { api, ApiError } from '@/lib/api';

/** A refused host action, with the server's `reason` when it gives one and, for the sign-off gate, whether hosts may override. */
export class HostActionError extends ApiError {
  readonly reason?: string;
  readonly overrideAllowed: boolean;
  constructor(status: number, message: string, reason?: string, overrideAllowed = false, body: Record<string, unknown> = {}) {
    super(status, message, body);
    this.reason = reason; this.overrideAllowed = overrideAllowed;
  }
}

/** True for a start refused because the battle's questions are waiting for clinical sign-off. */
export const isSignOffRefusal = (e: unknown): e is HostActionError => e instanceof HostActionError && e.reason === 'signoff-pending';

/** What a host action may carry: the sign-off override on start, the time on schedule (null clears it; 18 Oct 2026). */
export interface HostActionOptions { override?: boolean; at?: number | null }
/** The request body for those options, or none. */
export function actionBody(opts: HostActionOptions): { body?: string } {
  const body = { ...(opts.override ? { override: true } : {}), ...(opts.at !== undefined ? { at: opts.at } : {}) };
  return Object.keys(body).length ? { body: JSON.stringify(body) } : {};
}

export async function sendHostAction(code: string, hostKey: string, action: HostAction, opts: HostActionOptions = {}): Promise<{ state?: StateSnapshot }> {
  try {
    return await api<{ state?: StateSnapshot }>(`/battles/${encodeURIComponent(code)}/host/${action}`, {
      method: 'POST', headers: { 'X-Host-Key': hostKey },
      ...actionBody(opts),
    });
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
    const body = e.body ?? {};
    // The gate's refusal says so in `reason`; an older server only says it in words.
    const reason = typeof body.reason === 'string' ? body.reason : e.status === 409 && /clinical sign-off/i.test(e.message) ? 'signoff-pending' : undefined;
    throw new HostActionError(e.status, e.message, reason, body.overrideAllowed === true, body);
  }
}
